/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { User } from 'app/core/models/essette-api';
import { UserService } from 'app/core/services/user-service';
import { ProviderService } from 'app/cd/services/provider-service';
import { MemberProviderRelationship, Provider } from 'app/cd/models/essette-api';
import { CareTeamParticipant } from 'app/cm/models/essette-api';
import { MemberService } from 'app/cd/services/member-service';
import { CaseService } from 'app/cm/services/case-service';
import { task } from 'app/shared/lib/decorators';
import { guid } from 'app/shared/lib/guid';

type CareTeamRelationship = {
    relationship: MemberProviderRelationship | null,
    target: {
        name: string | null,
        providerId: string | null,
        userId: string | null,
    }
}

@autoinject()
export class AddToCareTeam extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected caseId: string | null = null;
    @bindable.stringNull({ changeHandler: 'loadProviders' })
    protected memberId: string | null = null;

    @bindable.array({ defaultBindingMode: bindingMode.toView, changeHandler: 'filterParticipants' })
    protected currentCareTeamParticipants: CareTeamParticipant[] = [];

    protected relationships: MemberProviderRelationship[] = [];
    protected users: CareTeamRelationship[] = [];
    protected providers: CareTeamRelationship[] = [];

    protected valid: boolean = false;
    protected careTeamChanged: boolean = false;
    protected insertCareTeam: CareTeamRelationship[] = [];


    /** Holds the value of the selected radio input. Switches between how user adds Users or Providers */
    protected teamOption: string = 'selectUsers';

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly providerService: ProviderService,
        private readonly memberService: MemberService,
        private readonly caseService: CaseService,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.loadRelationships();
        this.loadNonInternalUsers();
        if(this.memberId) {
            this.loadProviders();
        }
    }

    public filterParticipants(): void {
        this.users = this.users.filter(user => !this.currentCareTeamParticipants.find(u => u.assigned?.toUser?.id === user.target.userId));
        this.providers = this.providers.filter(provider => !this.currentCareTeamParticipants.find(p => p.assigned?.toProvider?.id === provider.target.providerId));
    }

    ////****************** Functions ************************************************////
    @task // INFO using Matt's trick from ess-select to let the bindings finish before validation triggers
    protected validateRelationships() {
        this.valid = this.insertCareTeam.length > 0 && this.insertCareTeam.every(participant => participant.relationship !== null)
    }

    protected buildNewCareTeamParticipants(): CareTeamParticipant[] {
        let careTeam: CareTeamParticipant[] = [];

        careTeam = this.insertCareTeam.map(participant => {
            const returnedParticipant: CareTeamParticipant = {
                case: {
                    id: this.caseId
                },
                relationship: {
                    id: participant.relationship?.id
                },
                assigned: {
                    toProvider: null,
                    toUser: null
                },
            }

            if(participant.target.userId) {
                let user: User = {
                    id: participant.target.userId
                }
                returnedParticipant.assigned!.toUser = user;
            }
            if(participant.target.providerId) {
                let provider: Provider = {
                    id: participant.target.providerId
                }
                returnedParticipant.assigned!.toProvider = provider;
            }

            return returnedParticipant;
        })

        return careTeam;
    }

    protected removeCareTeamParticipant(participant: CareTeamRelationship): void {
        const index: number = this.insertCareTeam.indexOf(participant, 0);

        this.insertCareTeam.splice(index, 1);
        this.validateRelationships();
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.providers = [];
        this.insertCareTeam = [];
        this.valid = false;
        this.teamOption = 'selectUsers'
        this.users.forEach(user => {
            user.relationship = null;
        });
    }

    public async submit(): Promise<boolean> {
        let success: boolean = true;
        let participants: CareTeamParticipant[] = [];

        participants = this.buildNewCareTeamParticipants();

        for(const participant of participants) {
            success = success && await this.insertCareTeamParticipant(participant);
        }

        return success;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadNonInternalUsers(): Promise<void> {
        const includes: string[] = ['login'];
        const fields: string[] = ['name.full', 'login.loginName'];

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        try {
            this.state.beginLoad();

            // Load Non Internal Users
            const searchFilter = {
                notInternal: true
            }
            const response = await this.userService.loadUsers(options, includes, searchFilter, fields);
            const users = response.results || null;

            this.users = users ? users.map(user => {
                return {
                    relationship: null,
                    target: {
                        name: user.name?.full,
                        nameDisplay: user.login?.loginName ? `${user.name?.full} (${user.login?.loginName})` : user.name?.full,
                        providerId: null,
                        userId: user.id
                    }
                } as CareTeamRelationship
            }) : []

            this.users = this.users.filter(user => !this.currentCareTeamParticipants.find(u => u.assigned?.toUser?.id === user.target.userId));

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadProviders(): Promise<void> {
        if(!this.memberId) return;

        const sorting = new Sorting();
        sorting.add({
            reference: 'provider.name.full',
            direction: SortDirection.Ascending
        });

        const includes: string[] = [
            'provider',
            'relationship'
        ];

        const fields: string[] = [
            'provider.name.full',
            'relationship.description',
        ];

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: sorting
        };

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadMemberProvidersByMember(this.memberId, options, includes, fields);
            const providers = response.results || null;

            this.providers = providers ? providers.map(provider => {
                return {
                    relationship: provider.relationship,
                    target: {
                        name: provider.provider?.name?.full + ' (' + provider.relationship?.description + ')',
                        providerId: provider.provider?.id,
                        userId: null
                    }
                } as CareTeamRelationship
            }) : []
            this.providers = this.providers.filter(provider => !this.currentCareTeamParticipants.find(p => p.assigned?.toProvider?.id === provider.target.providerId));

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadRelationships(): Promise<void> {
        try {
            this.state.beginLoad();

            const fields: string[] = ['description'];
            const response = await this.memberService.loadMemberProviderRelationships(undefined, [], fields);
            this.relationships = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async insertCareTeamParticipant(careTeamParticipant: CareTeamParticipant): Promise<boolean> {
        if(!this.insertCareTeam) return false;

        try {
            this.state.beginSave();

            const response = await this.caseService.insertCareTeamParticipant(careTeamParticipant);

            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
